import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import API_BASE_URL from "config/api";
import PropTypes from "prop-types";
import { useAuth } from "layouts/authentication/auth/auth";

const ManagementUnitContext = createContext();

export function ManagementUnitProvider({ children }) {
  const [hierarchy, setHierarchy] = useState([]);
  // Load from LocalStorage if available
  const [selectedUnit, setSelectedUnit] = useState(() => {
    const saved = localStorage.getItem("selectedUnit");
    try {
      return saved ? JSON.parse(saved) : null;
    } catch (e) {
      // If parsing fails, it might be the old format (just ID string)
      return saved ? { id: saved } : null;
    }
  });
  const [selectedUnitName, setSelectedUnitName] = useState(() => {
    return localStorage.getItem("selectedUnitName") || "Global";
  });
  const [isLoading, setIsLoading] = useState(true);

  const { user, vekalet } = useAuth();

  const fetchHierarchy = async () => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API_BASE_URL}/management-units/hierarchy`, {
        withCredentials: true,
      });
      setHierarchy(response.data);

      if (user && !user.is_root_admin && !selectedUnit && response.data.length > 0) {
        const defaultUnit = response.data[0];
        const unitObj = { id: defaultUnit.ID, name: defaultUnit.Name };
        setSelectedUnit(unitObj);
        setSelectedUnitName(defaultUnit.Name);
        localStorage.setItem("selectedUnit", JSON.stringify(unitObj));
        localStorage.setItem("selectedUnitName", defaultUnit.Name);
      }
    } catch (error) {
      console.error("Failed to fetch hierarchy", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchHierarchy();
  }, [user]);

  const refreshHierarchy = async () => {
    await fetchHierarchy();
  };

  // Helper to find name (recursive)
  const findNodeName = (nodes, id) => {
    for (let node of nodes) {
      if (String(node.ID) === String(id)) return node.Name;
      if (node.children) {
        const found = findNodeName(node.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  const handleUnitSelect = (nodeId) => {
    // We try to find name from hierarchy
    const name = findNodeName(hierarchy, nodeId);
    const newName = name || "Birim " + nodeId;

    const unitObj = { id: nodeId, name: newName };
    setSelectedUnit(unitObj);
    localStorage.setItem("selectedUnit", JSON.stringify(unitObj));

    setSelectedUnitName(newName);
    localStorage.setItem("selectedUnitName", newName);
  };

  const clearSelection = () => {
    if (user && !user.is_root_admin && hierarchy.length > 0) {
      // For non-root admins, clearing selection returns to their default unit (first one)
      const defaultUnit = hierarchy[0];
      handleUnitSelect(defaultUnit.ID);
    } else {
      setSelectedUnit(null);
      localStorage.removeItem("selectedUnit");
      setSelectedUnitName("Global");
      localStorage.removeItem("selectedUnitName");
    }
  };

  // Vekil bağlamında birim filtresi uygulanmaz: vekilin kendi birimi malikin
  // kiracılarını (ör. mesaj alıcıları) daraltmamalı. Kayıtlı seçim korunur,
  // kendi hesabına dönünce geri gelir.
  const value = {
    hierarchy,
    selectedUnit: vekalet ? null : selectedUnit,
    selectedUnitName,
    isLoading,
    handleUnitSelect,
    clearSelection,
    refreshHierarchy,
  };

  return <ManagementUnitContext.Provider value={value}>{children}</ManagementUnitContext.Provider>;
}

ManagementUnitProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useManagementUnit() {
  const context = useContext(ManagementUnitContext);
  if (!context) {
    throw new Error("useManagementUnit must be used within a ManagementUnitProvider");
  }
  return context;
}
